import { Input, Toggle } from '@teamsnap/snap-ui';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { Controller, FormContextValues } from 'react-hook-form';
import { NgbIntegration } from '../hooks/useApiGetNgbIntegrations';
import { DuplicateReg2FormModalInputs } from './DuplicateReg2FormModal';

type NgbIntegrationFieldsProps = {
  ngbIntegrationData: NgbIntegration;
  reactHookForm: FormContextValues<DuplicateReg2FormModalInputs>;
  formData: Partial<DuplicateReg2FormModalInputs>;
};

export default function NgbIntegrationFields(props: NgbIntegrationFieldsProps) {
  const { ngbIntegrationData, reactHookForm, formData } = props;
  const enabled = reactHookForm.watch('enabled');

  const parsedFields = React.useMemo(() => {
    const getDefaultValueForField = (
      code: 'usa_lacrosse' | 'usa_hockey' | 'hockey_canada',
      field: NgbIntegration['metadata']['formRequirements']['fields'][0]
    ): string => {
      if (code === 'usa_lacrosse' && field.name === 'season_dates') {
        const today = new Date();
        const oneYearFromNow = new Date(
          today.setFullYear(today.getFullYear() + 1)
        );
        return humanizeIsoDate(oneYearFromNow.toISOString());
      } else if (code === 'usa_hockey' && field.name === 'season_dates') {
        return parseOptionsPerNgbFieldName(code, field.name, field.options)[0].value;
      } else if (code === 'hockey_canada' && field.name === 'season_dates') {
        return parseOptionsPerNgbFieldName(code, field.name, field.options)[1].value;
      } else {
        return '';
      }
    };
    
    if (ngbIntegrationData.code === 'usa_lacrosse') {
      return ngbIntegrationData.metadata.formRequirements?.fields.map(
        (field) => ({
          ...field,
          options: [],
          defaultValue: getDefaultValueForField(ngbIntegrationData.code, field),
        })
      );
    }
    if (ngbIntegrationData.code === 'usa_hockey') {
      return ngbIntegrationData.metadata.formRequirements?.fields.map(
        (field) => ({
          ...field,
          options: parseOptionsPerNgbFieldName(ngbIntegrationData.code, field.name, field.options),
          defaultValue: getDefaultValueForField(ngbIntegrationData.code, field),
        })
      );
    }
    if (ngbIntegrationData.code === 'hockey_canada') {
      return ngbIntegrationData.metadata.formRequirements?.fields.map(
        (field) => ({
          ...field,
          options: parseOptionsPerNgbFieldName(ngbIntegrationData.code, field.name, field.options),
          defaultValue: getDefaultValueForField(ngbIntegrationData.code, field),
        })
      );
    }
    return [];
  }, [ngbIntegrationData]);

  // TODO: This useEffects can be deleted once we upgrade to react-hook-form v7
  React.useEffect(() => {
    reactHookForm.register('ngbIntegrationId');
    reactHookForm.register('ngbIntegrationCode');
    reactHookForm.register('enabled');
    return () => {
      reactHookForm.unregister('ngbIntegrationId');
      reactHookForm.unregister('ngbIntegrationCode');
      reactHookForm.unregister('enabled');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    reactHookForm.setValue('ngbIntegrationId', String(ngbIntegrationData.id));
    reactHookForm.setValue('ngbIntegrationCode', ngbIntegrationData.code);
    reactHookForm.setValue(
      'enabled',
      formData.enabled !== undefined ? formData.enabled : true
    );
    const seasonDatesField = parsedFields.find(
      (field) => field.name === 'season_dates'
    );
    reactHookForm.setValue(
      'season_dates',
      formData.season_dates || seasonDatesField?.defaultValue
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ngbIntegrationData]);

  return (
    <>
      <div className="sui-flex sui-items-center sui-gap-1">
        <p className="sui-label">
          {ngbIntegrationData.metadata.formRequirements?.toggleLabel}
        </p>
        <Toggle
          name="enabled"
          on={enabled}
          onClick={() => reactHookForm.setValue('enabled', !enabled)}
        />
      </div>
      {enabled && (
        <div className="sui-grid sui-gap-2 sui-mt-2">
          {parsedFields.map((field) => (
            <Controller
              key={`ngb-integration-field-for-duplication-${field.name}`}
              name={field.name}
              defaultValue={field.defaultValue}
              control={reactHookForm.control}
              rules={{
                required: field.required ? 'This field is required' : undefined,
              }}
              as={
                <Input
                  type={field.type}
                  name={field.name}
                  label={field.label}
                  helpText={field.adminHelpText}
                  required={field.required}
                  errorState={Boolean(reactHookForm.errors[field.name])}
                  options={field.options}
                  inputProps={{
                    ...(field.type === 'date' && {
                      min: format(new Date(), 'yyyy-MM-dd'),
                      max: format(
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 5)
                        ),
                        'yyyy-MM-dd'
                      ),
                    }),
                  }}
                />
              }
            />
          ))}
        </div>
      )}
    </>
  );
}

function parseOptionsPerNgbFieldName(
  code: 'usa_lacrosse' | 'usa_hockey' | 'hockey_canada',
  fieldName: keyof DuplicateReg2FormModalInputs,
  options?: { seasonStartDate: string; seasonEndDate: string }[]
): { label: string; value: string }[] {
  switch (fieldName) {
    case 'season_dates':
      return options?.map((option) => {
        const startDateToSet = parseISO(option.seasonStartDate);
        const endDateToSet = parseISO(option.seasonEndDate);

        if(code === 'hockey_canada') {
          return {
            label: `${format(startDateToSet, 'yyyy')} - ${format(
              endDateToSet,
              'yyyy'
            )}`,
            value: [option.seasonStartDate, option.seasonEndDate].join('|'),
          };
        }
        // parseISO will treat date as local time zone
        return {
          label: `${format(startDateToSet, 'yyyy')} - ${format(
            endDateToSet,
            'yyyy'
          )} (${format(startDateToSet, 'MMMM d yyyy')} - ${format(
            endDateToSet,
            'MMMM d yyyy'
          )})`,
          value: [option.seasonStartDate, option.seasonEndDate].join('|'),
        };
      });
    default:
      return [];
  }
}

function humanizeIsoDate(isoDate: string): string {
  const date = new Date(isoDate);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() returns 0-based months, so add 1
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
